import { useEffect, useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { Outlet, useRouterState } from '@tanstack/react-router';
import { X } from 'lucide-react';
import { Sidebar } from './Sidebar';
import { Header } from './Header';
import { NAV_ITEMS } from './nav-items';
import { Button } from '@/components/ui/Button';

/*
  The application shell.

  Responsive behaviour: at lg and above the sidebar is a permanent column. Below that it
  becomes an off-canvas drawer behind a menu button. The drawer is a Radix Dialog rather
  than a hand-rolled panel, which buys the three things a hand-rolled drawer always misses:
  focus is trapped inside it while open, escape closes it, and focus returns to the menu
  button afterwards.

  Route change announcement: a single-page application replaces the main region without a
  document load, so a screen reader is told nothing. The live region at the bottom announces
  the new page title on each navigation, which is the accepted pattern for SPA routing. It
  is aria-live=polite so it waits for a pause rather than interrupting.
*/

function pageTitleForPath(pathname: string): string {
  const match = NAV_ITEMS.find((item) =>
    item.to === '/' ? pathname === '/' : pathname.startsWith(item.to),
  );
  return match?.label ?? 'JIT Console';
}

export function AppShell(): React.JSX.Element {
  const [navOpen, setNavOpen] = useState(false);
  const pathname = useRouterState({ select: (state) => state.location.pathname });

  // Derived, not stored. Holding this in state and setting it from an effect
  // costs an extra render pass on every navigation and gives the announcement
  // nothing the render does not already know. A live region announces on
  // change after mount, so a derived value still reaches a screen reader.
  const title = pageTitleForPath(pathname);
  const announcement = `${title} page loaded`;

  // document.title is a genuine side effect on an object we do not own, so it
  // stays in an effect.
  useEffect(() => {
    document.title = `${title} | JIT Console`;
  }, [title]);

  return (
    <div className="flex min-h-dvh bg-canvas">
      <a href="#main-content" className="skip-link">
        Skip to main content
      </a>

      {/* Permanent sidebar from lg up. */}
      <aside className="hidden w-60 shrink-0 lg:block">
        <div className="fixed inset-y-0 left-0 w-60">
          <Sidebar />
        </div>
      </aside>

      {/* Off-canvas drawer below lg. */}
      <Dialog.Root open={navOpen} onOpenChange={setNavOpen}>
        <Dialog.Portal>
          <Dialog.Overlay className="animate-fade-in fixed inset-0 z-40 bg-[var(--color-overlay)] lg:hidden" />
          <Dialog.Content className="animate-fade-in fixed inset-y-0 left-0 z-50 w-72 max-w-[85vw] shadow-lg outline-none lg:hidden">
            <Dialog.Title className="sr-only">Navigation</Dialog.Title>
            <Dialog.Description className="sr-only">
              Primary sections of the JIT technician console.
            </Dialog.Description>
            <Dialog.Close asChild>
              <Button
                variant="ghost"
                size="sm"
                aria-label="Close navigation menu"
                className="absolute top-2.5 right-2 z-10"
              >
                <X className="size-4" aria-hidden="true" />
              </Button>
            </Dialog.Close>
            <Sidebar
              onNavigate={() => {
                setNavOpen(false);
              }}
            />
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>

      <div className="flex min-w-0 flex-1 flex-col">
        <Header
          onOpenNav={() => {
            setNavOpen(true);
          }}
        />
        <main
          id="main-content"
          // tabIndex -1 so the skip link can move focus here. Not in the tab order.
          tabIndex={-1}
          className="min-w-0 flex-1 px-4 py-6 outline-none sm:px-6 lg:px-8"
        >
          <Outlet />
        </main>
      </div>

      <div aria-live="polite" aria-atomic="true" className="sr-only">
        {announcement}
      </div>
    </div>
  );
}
